import KendoTable, {
  type ColumnConfig,
} from "@/app/components/common/table/KendoTable";
import {
  Checkbox,
  type CheckboxChangeEvent,
} from "@progress/kendo-react-inputs";
import {
  DateCellOne,
  NoOfApproverCell,
} from "@/app/components/common/table/KendoCustomCells";
import type { PayrollBulkApprovalViewSummaryResponse } from "@/app/types/bulkPaymentsPayroll";
import StatusBadge from "@/app/components/common/badge/StatusBadge";
import "@/app/page/default/Dashboard/Dashboard.scss";
import { gxCurrencyFormat } from "@/app/utils/helpers/gxCurrencyFormat";
import type { BulkUploadApproveTablePayrollProps } from "../BulkUpload";
import { t } from "i18next";

function payrollGrowXRefCell(props: {
  dataItem?: PayrollBulkApprovalViewSummaryResponse;
}) {
  return <td>{props.dataItem?.gxJobChnNo || "--"}</td>;
}

function payrollPeriodCell(props: {
  dataItem?: PayrollBulkApprovalViewSummaryResponse;
}) {
  const { jobStartDate, jobEndDate } = props.dataItem ?? {};

  if (!jobStartDate || !jobEndDate) {
    return (
      <span>
        <p className="gx-cell-date-text gx-p10-m">--</p>
      </span>
    );
  }

  const format = (d: string) =>
    new Date(d).toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });

  return (
    <span>
      <p className="gx-cell-date-text gx-p10-m">
        {format(jobStartDate)} - {format(jobEndDate)}
      </p>
    </span>
  );
}

function employeeCountCell(props: {
  dataItem?: PayrollBulkApprovalViewSummaryResponse;
}) {
  return <td className="text-center">{props.dataItem?.totalTxnCount || 0}</td>;
}

function payrollAmountCell(props: {
  dataItem?: PayrollBulkApprovalViewSummaryResponse;
}) {
  const dataItem = props.dataItem;
  const amount = dataItem?.totalAmount || 0;
  const currencyCode =
    (
      dataItem as PayrollBulkApprovalViewSummaryResponse & {
        baseCurrency?: string;
      }
    )?.baseCurrency || "--";
  const currencyData = gxCurrencyFormat(amount, currencyCode);

  return (
    <div className="flex flex-col">
      <div className="flex gx-amount-cell">
        <span className="gx-rtp-currency gx-p12-sb">{currencyData.symbol}</span>
        <span className="gx-text-ellipsis gx-rtp-amount gx-p12-sb">
          {currencyData.integer}
          <span className="gx-text-ellipsis gx-rtp-amount-sub gx-p12-sb">
            {currencyData.decimal}
          </span>
        </span>
      </div>
    </div>
  );
}

function validRecordsCell(props: {
  dataItem?: PayrollBulkApprovalViewSummaryResponse;
}) {
  return <td className="text-center">{props.dataItem?.validTxnCount || 0}</td>;
}

function invalidRecordsCell(props: {
  dataItem?: PayrollBulkApprovalViewSummaryResponse;
}) {
  return (
    <td className="text-center">{props.dataItem?.invalidTxnCount || 0}</td>
  );
}

function statusCell(props: {
  dataItem?: PayrollBulkApprovalViewSummaryResponse;
}) {
  return (
    <td>
      <StatusBadge module="bulk" statusCode={props.dataItem?.jobStatusCode} />
    </td>
  );
}

function fileNameCell(props: {
  dataItem?: PayrollBulkApprovalViewSummaryResponse;
}) {
  return (
    <td>
      <div className="d-flex align-items-center">
        <span className="gx-p12-b">{props.dataItem?.fileName}</span>
      </div>
    </td>
  );
}

const BulkUploadApproveTablePayroll: React.FC<
  BulkUploadApproveTablePayrollProps
> = ({
  onRowClick,
  showAll = false,
  selectedBulkUploads = [],
  setSelectedBulkUploads,
  setCheckAll,
  checkAll = false,
  approveAccess = false,
  bulkUploads,
  isLoading,
  maxSelectionLimit = 5,
  hideCbCheckbox = false,
}) => {
  function checkboxHeaderCell() {
    if (!approveAccess) return null;
    return (
      <Checkbox
        className="gx-font-medium"
        checked={checkAll}
        onChange={(event: CheckboxChangeEvent) => {
          if (!setCheckAll || !setSelectedBulkUploads) return;
          if (event.value) {
            const toSelect = bulkUploads
              .slice(0, maxSelectionLimit)
              .map((item) => item.gxJobProcessid);
            setSelectedBulkUploads(toSelect);
          } else {
            setSelectedBulkUploads([]);
            setCheckAll(false);
          }
        }}
        disabled={!approveAccess}
      />
    );
  }

  function checkboxRowCell(props: {
    dataItem?: PayrollBulkApprovalViewSummaryResponse;
  }) {
    const id = props.dataItem?.gxJobProcessid;
    if (id === undefined) return <td />;

    const isSelected = selectedBulkUploads.includes(id);
    const isDisabled =
      !isSelected && selectedBulkUploads.length >= maxSelectionLimit;

    return (
      <td className="check-box-cell" onClick={(e) => e.stopPropagation()}>
        {approveAccess && (
          <Checkbox
            checked={isSelected}
            disabled={isDisabled}
            onClick={(e) => e.stopPropagation()}
            onChange={(_event: CheckboxChangeEvent) => {
              if (!setSelectedBulkUploads) return;
              if (isSelected) {
                setSelectedBulkUploads(
                  selectedBulkUploads.filter((x) => x !== id),
                );
              } else if (selectedBulkUploads.length < maxSelectionLimit) {
                setSelectedBulkUploads([...selectedBulkUploads, id]);
              }
            }}
          />
        )}
      </td>
    );
  }

  const columns: ColumnConfig[] =
    showAll === false
      ? [
          {
            field: "gxJobChnNo",
            title: t("growx_ref_no"),
            width: 130,
            cell: payrollGrowXRefCell,
          },
          {
            field: "crtdDt",
            title: t("uploaded_on"),
            width: 100,
            cell: DateCellOne,
          },
          {
            field: "payrollPeriod",
            title: t("payroll_period"),
            width: 140,
            cell: payrollPeriodCell,
          },
          {
            field: "totalTxnCount",
            title: t("employee_count"),
            width: 130,
            cell: employeeCountCell,
          },
          {
            field: "totalAmount",
            title: t("payroll_amount"),
            width: 130,
            cell: payrollAmountCell,
          },
          {
            field: "validTxnCount",
            title: t("valid_records"),
            width: 100,
            cell: validRecordsCell,
          },
          {
            field: "invalidTxnCount",
            title: t("invalid_records"),
            width: 100,
            cell: invalidRecordsCell,
          },
          {
            field: "jobStatusDesc",
            title: t("status"),
            width: 150,
            cell: statusCell,
          },
          {
            field: "approvalLevel",
            title: t("approval_level"),
            width: 120,
            cell: NoOfApproverCell,
          },
          {
            field: "fileName",
            title: t("file_name"),
            width: 280,
            cell: fileNameCell,
          },
        ]
      : [
          ...(!hideCbCheckbox
            ? [
                {
                  field: "",
                  headerCell: checkboxHeaderCell(),
                  cell: checkboxRowCell,
                  width: 45,
                },
              ]
            : []), // Checkbox column - skip entirely when CB
          {
            field: "gxJobChnNo",
            title: t("growx_ref_no"),
            width: 130,
            cell: payrollGrowXRefCell,
          },
          {
            field: "crtdDt",
            title: t("uploaded_on"),
            width: 150,
            cell: DateCellOne,
          },
          {
            field: "payrollPeriod",
            title: t("payroll_period"),
            width: 160,
            cell: payrollPeriodCell,
          },
          {
            field: "totalTxnCount",
            title: t("employee_count"),
            width: 130,
            cell: employeeCountCell,
          },
          {
            field: "totalAmount",
            title: t("payroll_amount"),
            width: 150,
            cell: payrollAmountCell,
          },
          {
            field: "validTxnCount",
            title: t("valid_records"),
            width: 140,
            cell: validRecordsCell,
          },
          {
            field: "invalidTxnCount",
            title: t("invalid_records"),
            width: 140,
            cell: invalidRecordsCell,
          },
          {
            field: "jobStatusDesc",
            title: t("status"),
            width: 130,
            cell: statusCell,
          },
          {
            field: "approvalLevel",
            title: t("approval_level"),
            width: 120,
            cell: NoOfApproverCell,
          },
          {
            field: "fileName",
            title: t("file_name"),
            width: 280,
            cell: fileNameCell,
          },
        ];

  if (isLoading) {
    return (
      <div className="text-center p-5">
        <output className="spinner-border text-primary">
          <span className="gx-visually-hidden gx-font-regular">{t("loading__")}</span>
        </output>

        <p className="mt-2">{t("loading_payroll_data")}</p>
      </div>
    );
  }

  return (
    <>
      {selectedBulkUploads.length > 0 && (
        <div className="mb-3 p-2 bg-light rounded">
          <span className="gx-p12-b">
            {selectedBulkUploads.length} {t("of_text")} {maxSelectionLimit}{" "}
            {t("records_selected")}
          </span>
        </div>
      )}

      <KendoTable
        data={bulkUploads}
        columns={columns}
        onRowClick={onRowClick}
        className="kendo-grid-bulk-uploads-payroll"
      />
    </>
  );
};

export default BulkUploadApproveTablePayroll;
